import { Plus, X, ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';

interface AgentRule {
  id: string;
  text: string;
}

interface AgentRulesSectionProps {
  doRules: AgentRule[];
  dontRules: AgentRule[];
  onDoRulesChange: (rules: AgentRule[]) => void;
  onDontRulesChange: (rules: AgentRule[]) => void;
  subtitle?: string;
}

export function AgentRulesSection({
  doRules,
  dontRules,
  onDoRulesChange,
  onDontRulesChange,
  subtitle = "Define what your agent should and shouldn't do",
}: AgentRulesSectionProps) {
  return (
    <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
          <ShieldCheck className="w-5 h-5 text-[#1875FF]" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900 font-display">Agent Rules</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-emerald-50 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <h4 className="text-sm font-bold text-gray-900 font-display">Do This</h4>
              {doRules.length > 0 && (
                <span className="text-xs text-gray-400 font-medium">{doRules.length}/7</span>
              )}
            </div>
            {doRules.length < 7 && (
              <button
                onClick={() => onDoRulesChange([...doRules, { id: crypto.randomUUID(), text: '' }])}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1875FF] hover:text-[#05E0D1] rounded-lg hover:bg-[#1875FF]/5 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Rule
              </button>
            )}
          </div>

          {doRules.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <CheckCircle2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No rules added yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Add Rule" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {doRules.map((rule, index) => (
                <div key={rule.id} className="flex items-start gap-2.5 group">
                  <span className="mt-2.5 text-xs font-semibold text-emerald-500 w-5 text-right shrink-0">{index + 1}.</span>
                  <textarea
                    value={rule.text}
                    onChange={(e) => {
                      const updated = doRules.map(r =>
                        r.id === rule.id ? { ...r, text: e.target.value } : r
                      );
                      onDoRulesChange(updated);
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="E.g., Always confirm the prospect's availability before booking"
                    className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm resize-none overflow-hidden"
                    rows={1}
                  />
                  <button
                    onClick={() => onDoRulesChange(doRules.filter(r => r.id !== rule.id))}
                    className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-red-50 rounded-lg">
                <XCircle className="w-4 h-4 text-red-500" />
              </div>
              <h4 className="text-sm font-bold text-gray-900 font-display">Don't Do This</h4>
              {dontRules.length > 0 && (
                <span className="text-xs text-gray-400 font-medium">{dontRules.length}/7</span>
              )}
            </div>
            {dontRules.length < 7 && (
              <button
                onClick={() => onDontRulesChange([...dontRules, { id: crypto.randomUUID(), text: '' }])}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#1875FF] hover:text-[#05E0D1] rounded-lg hover:bg-[#1875FF]/5 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Rule
              </button>
            )}
          </div>

          {dontRules.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <XCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No rules added yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Add Rule" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dontRules.map((rule, index) => (
                <div key={rule.id} className="flex items-start gap-2.5 group">
                  <span className="mt-2.5 text-xs font-semibold text-red-400 w-5 text-right shrink-0">{index + 1}.</span>
                  <textarea
                    value={rule.text}
                    onChange={(e) => {
                      const updated = dontRules.map(r =>
                        r.id === rule.id ? { ...r, text: e.target.value } : r
                      );
                      onDontRulesChange(updated);
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="E.g., Never discuss competitor pricing or make guarantees"
                    className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm resize-none overflow-hidden"
                    rows={1}
                  />
                  <button
                    onClick={() => onDontRulesChange(dontRules.filter(r => r.id !== rule.id))}
                    className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
